import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, PutCommand, GetCommand, ScanCommand, DeleteCommand, UpdateCommand } from '@aws-sdk/lib-dynamodb';
import { fetchAuthSession } from 'aws-amplify/auth';

const isDev = process.env.NODE_ENV === 'development' || 
              window.location.hostname === 'localhost' ||
              window.location.hostname.includes('dev.d1d7vib6srssm3');
const TABLE_NAME = isDev ? 'asl-ceu-tracker-email-templates-dev' : 'asl-ceu-tracker-email-templates';

// Get DynamoDB client with Amplify credentials
const getDynamoClient = async () => {
  const session = await fetchAuthSession();
  const credentials = session.credentials;
  
  const client = new DynamoDBClient({
    region: process.env.REACT_APP_AWS_REGION,
    credentials
  });
  
  return DynamoDBDocumentClient.from(client);
};

// Get current user email
const getCurrentUserEmail = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.payload?.email || 'system';
  } catch (error) {
    return 'system';
  }
};

// Create a new email template
export const createTemplate = async (templateData) => {
  try {
    const docClient = await getDynamoClient();
    const userEmail = await getCurrentUserEmail();

    const template = {
      templateId: templateData.templateId,
      templateName: templateData.templateName,
      subject: templateData.subject,
      bodyHtml: templateData.bodyHtml,
      bodyText: templateData.bodyText,
      category: templateData.category || 'general',
      isActive: templateData.isActive !== undefined ? templateData.isActive : true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: userEmail,
      lastSentAt: null,
      sentCount: 0
    };

    const command = new PutCommand({
      TableName: TABLE_NAME,
      Item: template
    });

    await docClient.send(command);
    return template;
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
};

// Get a single template by ID
export const getTemplate = async (templateId) => {
  try {
    const docClient = await getDynamoClient();
    const command = new GetCommand({
      TableName: TABLE_NAME,
      Key: { templateId }
    });

    const response = await docClient.send(command);
    return response.Item || null;
  } catch (error) {
    console.error('Error getting template:', error);
    throw error;
  }
};

// Get all templates
export const getAllTemplates = async () => {
  try {
    const docClient = await getDynamoClient();
    const command = new ScanCommand({
      TableName: TABLE_NAME
    });

    const response = await docClient.send(command);
    return response.Items || [];
  } catch (error) {
    console.error('Error getting templates:', error);
    throw error;
  }
};

// Update a template
export const updateTemplate = async (templateId, updates) => {
  try {
    const docClient = await getDynamoClient();
    
    // Remove templateId from updates if present (it's the primary key)
    const { templateId: _, ...updateData } = updates;
    
    const updateExpression = [];
    const expressionAttributeNames = {};
    const expressionAttributeValues = {};

    // Build update expression dynamically
    Object.keys(updateData).forEach((key, index) => {
      updateExpression.push(`#attr${index} = :val${index}`);
      expressionAttributeNames[`#attr${index}`] = key;
      expressionAttributeValues[`:val${index}`] = updateData[key];
    });

    // Always update the updatedAt timestamp
    updateExpression.push(`#updatedAt = :updatedAt`);
    expressionAttributeNames['#updatedAt'] = 'updatedAt';
    expressionAttributeValues[':updatedAt'] = new Date().toISOString();

    const command = new UpdateCommand({
      TableName: TABLE_NAME,
      Key: { templateId },
      UpdateExpression: `SET ${updateExpression.join(', ')}`,
      ExpressionAttributeNames: expressionAttributeNames,
      ExpressionAttributeValues: expressionAttributeValues,
      ReturnValues: 'ALL_NEW'
    });

    const response = await docClient.send(command);
    return response.Attributes;
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

// Delete a template
export const deleteTemplate = async (templateId) => {
  try {
    const docClient = await getDynamoClient();
    const command = new DeleteCommand({
      TableName: TABLE_NAME,
      Key: { templateId }
    });

    await docClient.send(command);
    return true;
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

// Increment sent count (called when template is used)
export const incrementSentCount = async (templateId) => {
  try {
    const docClient = await getDynamoClient();
    const command = new UpdateCommand({
      TableName: TABLE_NAME,
      Key: { templateId },
      UpdateExpression: 'SET sentCount = if_not_exists(sentCount, :zero) + :inc, lastSentAt = :now',
      ExpressionAttributeValues: {
        ':inc': 1,
        ':zero': 0,
        ':now': new Date().toISOString()
      },
      ReturnValues: 'ALL_NEW'
    });

    const response = await docClient.send(command);
    return response.Attributes;
  } catch (error) {
    console.error('Error incrementing sent count:', error);
    throw error;
  }
};

// Replace template variables with actual data
export const renderTemplate = (template, userData) => {
  let renderedHtml = template.bodyHtml;
  let renderedText = template.bodyText;
  let renderedSubject = template.subject;

  // Replace variables like {{firstName}}, {{cycleEndDate}}, etc.
  Object.keys(userData).forEach(key => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    renderedHtml = renderedHtml.replace(regex, userData[key] || '');
    renderedText = renderedText.replace(regex, userData[key] || '');
    renderedSubject = renderedSubject.replace(regex, userData[key] || '');
  });

  return {
    subject: renderedSubject,
    bodyHtml: renderedHtml,
    bodyText: renderedText
  };
};
